import { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CMPStep5ConstructionWaste = ({ project, onComplete, onBack }) => {
  const [saving, setSaving] = useState(false);
  const [totalGFA, setTotalGFA] = useState('');
  const [constructionType, setConstructionType] = useState('mixed_use');
  const [numStoreys, setNumStoreys] = useState(1);
  const [buildingDescription, setBuildingDescription] = useState('');

  useEffect(() => {
    // Load existing data
    if (project?.cmp_data) {
      if (project.cmp_data.total_gfa) setTotalGFA(project.cmp_data.total_gfa.toString());
      if (project.cmp_data.construction_type) setConstructionType(project.cmp_data.construction_type);
      if (project.cmp_data.num_storeys) setNumStoreys(project.cmp_data.num_storeys);
      if (project.cmp_data.building_description) setBuildingDescription(project.cmp_data.building_description);
    }
    // Also check step1_data for GFA
    if (project?.step1_data?.total_gfa) {
      setTotalGFA(project.step1_data.total_gfa.toString());
    }
  }, [project]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!totalGFA || parseFloat(totalGFA) <= 0) {
      alert('Please enter a valid GFA');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('opwmp_token');
      
      // Save CMP data
      await axios.post(`${API}/projects/${project.id}/cmp/step-data`, {
        total_gfa: parseFloat(totalGFA),
        construction_type: constructionType,
        num_storeys: numStoreys,
        building_description: buildingDescription.trim() || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update step
      await axios.put(`${API}/projects/${project.id}/step`, {
        step_number: 6
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      onComplete({ ...project, current_step: 6 });
    } catch (error) {
      console.error('Error saving:', error);
      alert('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const constructionTypes = [
    { value: 'residential_lowrise', label: 'Residential (Low-rise)' },
    { value: 'residential_midrise', label: 'Residential (Mid-rise)' },
    { value: 'commercial', label: 'Commercial' },
    { value: 'mixed_use', label: 'Mixed Use' }
  ];

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        {/* Back button at top */}
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Construction Waste Management</h2>
        <p className="text-gray-600 mb-8">
          Enter project details for construction waste estimation. Detailed calculations will be included in your final CMP document.
        </p>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Input Section */}
          <div className="border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Project Parameters
            </h3>
            
            {/* Building Description - Optional */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Building/Project Description <span className="text-gray-400 font-normal">(Optional)</span>
              </label>
              <input
                type="text"
                value={buildingDescription}
                onChange={(e) => setBuildingDescription(e.target.value)}
                placeholder="e.g., High School, 3 Storey Residential Building, Mixed-Use Development"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500">This will appear in Section 6 of your CMP document</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Total GFA (m²) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={totalGFA}
                  onChange={(e) => setTotalGFA(e.target.value)}
                  placeholder="e.g., 2500"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Construction Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={constructionType}
                  onChange={(e) => setConstructionType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {constructionTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Number of Storeys <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min="1"
                  value={numStoreys}
                  onChange={(e) => setNumStoreys(parseInt(e.target.value) || 1)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex">
              <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h4 className="text-sm font-medium text-blue-900">Waste Calculations</h4>
                <p className="text-sm text-blue-700 mt-1">
                  Detailed construction waste estimates, material volumes, and diversion rates will be calculated and included in your final CMP document after payment.
                </p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-between pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onBack}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Back
            </button>
            <button
              type="submit"
              disabled={saving || !totalGFA}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Continue'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CMPStep5ConstructionWaste;
